<template>
  <b-card title="Mail Templates">
    <!-- Filter row -->
    <b-row>
      <b-col md="4">
        <b-form-group label="Search">
          <b-form-input
            v-model="searchQuery"
            type="text"
            placeholder="Search by name or description"
            @input="searchChangeHandler"
          />
        </b-form-group>
      </b-col>
    </b-row>

    <div
      v-if="loading"
      class="text-center"
    >
      <b-spinner
        label="Small Spinner"
        variant="primary"
      />
    </div>

    <b-alert
      variant="danger"
      :show="loadingError ? true : false"
    >
      <div class="alert-body">
        <p>
          {{ loadingError }}
        </p>
      </div>
    </b-alert>

    <add-email-template
      v-if="showTemplateForm"
      :template-to-edit="templateToEdit"
      @modal-closed="closeTemplateForm"
      @template-saved="templateSavedHandler"
    />
    <hr>
    <b-row>
      <b-col align="right">
        <b-button
          variant="outline-primary"
          class="mb-1 ml-2"
          @click="openAddForm"
        >
          Add Email Template
        </b-button>
      </b-col>
    </b-row>

    <b-table
      v-if="!loading && !loadingError"
      :items="results"
      :fields="tableFields"
      responsive="sm"
      striped
      hover
      :sort-by="sortBy"
      :sort-desc="sortDesc"
      class="compact-table"
      no-local-sorting
      @sort-changed="sortingChanged"
    >
      <template #cell(description)="data">
        <span
          v-b-tooltip.hover
          :title="data.item.description"
        >
          {{ truncate(data.item.description, 60) }}
        </span>
      </template>

      <template #cell(created_at)="data">
        {{ formatedDate(data.item.created_at) }}
      </template>

      <template #cell(actions)="data">
        <b-button
          v-b-tooltip.hover
          size="sm"
          variant="flat-primary"
          class="btn-icon"
          title="Edit"
          @click="openEditForm(data.item)"
        >
          <feather-icon
            icon="EditIcon"
            size="16"
          />
        </b-button>
      </template>
    </b-table>

    <detailed-pagination
      v-if="!loading && !loadingError"
      :total-items="totalItems"
      :per-page="perPage"
      :current-page="currentPage"
      @page-changed="(page) => {currentPage = page}"
    />
  </b-card>
</template>

<script>
import {
  BCard, BSpinner, BTable, BAlert, BRow, BCol, BButton,
  BFormGroup, BFormInput, VBTooltip,
} from 'bootstrap-vue'
import axios from 'axios'

import dateMixin from '@/mixins/dateMixin'
import formatMixin from '@/mixins/formatMixin'
import DetailedPagination from '@/components/UI/DetailedPagination.vue'
import AddEmailTemplate from '@/components/Communication/MailTemplates/AddEmailTemplate.vue'

export default {
  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    BCard,
    BSpinner,
    BTable,
    BRow,
    BCol,
    BButton,
    BAlert,
    BFormGroup,
    BFormInput,
    DetailedPagination,
    AddEmailTemplate,
  },
  mixins: [dateMixin, formatMixin],
  data() {
    return {
      loading: true,
      loadingError: null,

      // Filter state (kept inline, no separate filter component)
      searchQuery: '',
      filters: {},

      results: [],
      tableFields: [
        { key: 'name', label: 'Name', sortable: true },
        { key: 'description', label: 'Description', sortable: false },
        { key: 'created_at', label: 'Created On', sortable: true },
        {
          key: 'actions', label: 'Actions', sortable: false, class: 'text-center', thStyle: { width: '100px' },
        },
      ],
      currentPage: 1,
      perPage: 50,
      totalItems: 0,
      sortBy: 'created_at',
      sortDesc: true,

      showTemplateForm: false,
      templateToEdit: null,

      // Debounce handle for search input
      searchDebounceTimer: null,
    }
  },
  watch: {
    currentPage() {
      this.fetchItems()
    },
    filters() {
      if (this.currentPage !== 1) {
        this.currentPage = 1
      } else {
        this.fetchItems()
      }
    },
  },
  created() {
    this.fetchItems()
  },
  methods: {
    // --- Filter handler (debounced for text input) ---
    searchChangeHandler() {
      if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer)
      this.searchDebounceTimer = setTimeout(() => {
        this.filtersUpdated()
      }, 400)
    },

    filtersUpdated() {
      const filtersData = {}
      if (this.searchQuery && this.searchQuery.trim()) {
        filtersData.search = this.searchQuery.trim()
      }
      this.filters = filtersData
    },

    sortingChanged(ctx) {
      this.sortBy = ctx.sortBy
      this.sortDesc = ctx.sortDesc
      this.fetchItems()
    },

    fetchItems() {
      this.loading = true
      axios.get('/emails/email_templates/', {
        params: {
          page: this.currentPage,
          per_page: this.perPage,
          sort_by: this.sortBy,
          sort_desc: this.sortDesc,
          ...this.filters,
        },
      })
        .then(res => {
          // Handle both paginated and non-paginated responses
          if (Array.isArray(res.data)) {
            this.results = res.data
            this.totalItems = res.data.length
          } else {
            this.results = res.data.results || []
            this.totalItems = res.data.count || 0
          }
          this.loadingError = null
          this.loading = false
        }).catch(error => {
          this.loadingError = error?.response?.data?.error || 'Something went wrong'
          this.loading = false
        })
    },

    openAddForm() {
      this.templateToEdit = null
      this.showTemplateForm = true
    },

    openEditForm(template) {
      this.templateToEdit = { ...template }
      this.showTemplateForm = true
    },

    closeTemplateForm() {
      this.showTemplateForm = false
      this.templateToEdit = null
    },

    templateSavedHandler() {
      this.closeTemplateForm()
      this.fetchItems()
    },

    truncate(text, length) {
      if (!text) return ''
      return text.length > length ? `${text.substring(0, length)}...` : text
    },
  },
}
</script>
